export const WORK_STATUSES = ['todo', 'in_progress', 'blocked', 'completed', 'cancelled'] as const;
export type WorkStatus = (typeof WORK_STATUSES)[number];
export type WorkPriority = 'low' | 'normal' | 'high' | 'urgent';
export interface WorkChecklistItem {
  id: string;
  text: string;
  done: boolean;
}
export interface WorkTask {
  id: string;
  version: number;
  updated_at: string;
  plan_id: string;
  plan_title?: string;
  plan_target_date?: string | null;
  plan_is_archived: boolean;
  title: string;
  description: string | null;
  status: WorkStatus;
  progress: number;
  priority: WorkPriority;
  due_date: string | null;
  is_overdue: boolean;
  blocker_note: string | null;
  reopen_note?: string | null;
  checklist: WorkChecklistItem[];
  checklist_error?: string;
  assignee_id: string | null;
  assignee_name: string | null;
  assignee_role?: string | null;
  created_at: string;
  completed_at: string | null;
}

export const WORK_TRANSITIONS: Record<WorkStatus, readonly WorkStatus[]> = {
  todo: ['in_progress', 'blocked', 'completed', 'cancelled'],
  in_progress: ['todo', 'blocked', 'completed', 'cancelled'],
  blocked: ['todo', 'in_progress', 'completed', 'cancelled'],
  completed: ['todo', 'in_progress'],
  cancelled: ['todo'],
};

export function workTransitionAllowed(current: WorkStatus, next: WorkStatus, canManage: boolean) {
  if (current === next) return true;
  if (!WORK_TRANSITIONS[current]?.includes(next)) return false;
  return canManage || (next !== 'cancelled' && !['completed', 'cancelled'].includes(current));
}

export function validateWorkProgress(input: {
  currentStatus: WorkStatus;
  status: WorkStatus;
  progress: number;
  canManage: boolean;
  blockerNote?: string | null;
  reopenNote?: string | null;
}): string | undefined {
  if (
    !WORK_STATUSES.includes(input.status) ||
    !workTransitionAllowed(input.currentStatus, input.status, input.canManage)
  )
    return 'This status change is not allowed. Reopen cancelled work as To do through a manager.';
  if (!Number.isInteger(input.progress) || input.progress < 0 || input.progress > 100)
    return 'Enter a whole progress percentage from 0 to 100.';
  if (input.status === 'todo' && input.progress !== 0) return 'To do tasks must have 0% progress.';
  if (input.status === 'completed' && input.progress !== 100)
    return 'Completed tasks must have 100% progress.';
  if (input.status === 'in_progress' && (input.progress < 1 || input.progress > 99))
    return 'In progress tasks need a percentage from 1 to 99.';
  if (input.status === 'blocked') {
    if (input.progress > 99) return 'Blocked tasks need a percentage from 0 to 99.';
    const note = input.blockerNote?.trim() ?? '';
    if (note.length < 5 || note.length > 1000) return 'Enter a blocker note of 5–1,000 characters.';
  }
  if (
    input.currentStatus !== input.status &&
    ['completed', 'cancelled'].includes(input.currentStatus)
  ) {
    const note = input.reopenNote?.trim() ?? '';
    if (note.length < 3 || note.length > 500)
      return 'Enter a reopening reason of 3–500 characters.';
  }
}

export function workBusinessToday(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);
  const value = (kind: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === kind)?.value ?? '';
  return `${value('year')}-${value('month')}-${value('day')}`;
}

/** A rounded percentage must never imply completion while eligible work remains. */
export function workCompletionPercent(completed: number, total: number): number {
  if (total <= 0) return 0;
  if (completed >= total) return 100;
  return Math.min(99.9, Math.round((completed / total) * 1000) / 10);
}

/** Keep malformed legacy JSON visible as an error rather than crashing or overwriting it. */
export function normalizeWorkTask(value: WorkTask): WorkTask {
  const entries: unknown = value.checklist;
  const valid =
    !value.checklist_error &&
    Array.isArray(entries) &&
    entries.length <= 30 &&
    entries.every(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        typeof entry.id === 'string' &&
        entry.id.length > 0 &&
        entry.id.length <= 64 &&
        Object.keys(entry).every((key) => ['id', 'text', 'done'].includes(key)) &&
        typeof entry.text === 'string' &&
        entry.text.trim().length > 0 &&
        entry.text.length <= 160 &&
        typeof entry.done === 'boolean',
    ) &&
    new Set(entries.map((entry) => entry.id)).size === entries.length;
  return {
    ...value,
    checklist: valid ? (entries as WorkChecklistItem[]) : [],
    checklist_error: valid
      ? undefined
      : 'This task has an unsupported checklist. Ask a manager to reconcile it before editing checklist details.',
  };
}
